import { addDisplayName, addHandlers, addProps, flowMax } from 'ad-hok'
import cx from 'classnames'
import { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { CustomLink as CustomLinkType } from 'shared/src/api/types'
import { trackOutgoingClick } from 'shared/src/utils/analytics'

import rightChevron from 'assets/right-chevron.svg'
import { LinkOrButton } from 'components/LinkOrButton'
import { serifFontFamily } from 'utils/styles'
import { isInternalUrl } from 'utils/urls'

interface StyleProps {
  backgroundImage?: string
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#1E1E1E',
    height: 84,
    marginLeft: 24,
    marginRight: 24,
    marginBottom: 12,
    borderRadius: 12,
    paddingLeft: 29,
    paddingRight: 20,
    backgroundImage: ({ backgroundImage }: StyleProps) =>
      backgroundImage ? `url(${backgroundImage})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  name: {
    fontFamily: serifFontFamily,
    fontSize: 18,
    fontWeight: 500,
    color: 'white',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'center',
  },
})

interface Props {
  link: CustomLinkType
  className?: string
}

export const CustomLink: FC<Props> = flowMax(
  addDisplayName('CustomLink'),
  addApi,
  addHandlers({
    onClick:
      ({ link, api }) =>
      () => {
        trackOutgoingClick(
          {
            url: link.url,
            creatorId: link.userId,
          },
          api
        )
      },
  }),
  addProps(({ link }) => ({
    isInternalUrl: isInternalUrl(link.url),
  })),
  addProps(({ link }) => ({
    classes: useStyles({
      backgroundImage: link.image?.full,
    }),
  })),
  ({ link, onClick, isInternalUrl, className, classes }) => (
    <LinkOrButton
      navigateTo={link.url}
      onClick={onClick}
      external={!isInternalUrl}
      /// This causes a full app reload which clears the navigation state (ex. back button disappears)
      /// Internal URLs for custom links are exclusively used for stealth profiles at the moment so this is fine
      /// but could be problematic if we ever use internal URLs for non-stealth profiles
      reloadDocument={isInternalUrl}
      noFollow
      className={cx(classes.container, className)}
    >
      <div className={classes.name}>{link.name}</div>
      <img src={rightChevron} alt="" />
    </LinkOrButton>
  )
)
